// The public website API, `/v1` (16 §16.2). Request and response shapes for the HTTPS function
// `publicApi` (asia-southeast1). The OpenAPI 3.1 document at `docs/api/public-v1.openapi.json` is
// generated from these schemas, so the contract cannot drift from the code.
//
// Rules for every route: JSON in and out; errors are `{ok: false, code, message}`; nothing here
// ever returns a staff name, a staff phone, a customer name, an address line, a customer phone or an
// amount of money charged to someone. A quote is an estimate and says so.
import { z } from 'zod';
import { branchCodeSchema, sectorCodeSchema, SERVICES, sizeCodeSchema, slugSchema } from './config';
import { e164Schema, trackingNoSchema } from './cargo';
import { campaignCodeSchema } from './customerConfig';

const iso = z.string();

export const PUBLIC_API_VERSION = '1.0.0';

/** A phone typed by a person: spaces, dashes and brackets are dropped, then it must be E.164. */
export const publicPhoneSchema = z
  .string()
  .max(40)
  .transform((s) => s.replace(/[\s\-().]/g, ''))
  .pipe(e164Schema);

export const publicErrorSchema = z
  .object({ ok: z.literal(false), code: z.string(), message: z.string() })
  .strict();
export type PublicError = z.infer<typeof publicErrorSchema>;

/** Error codes the website can branch on. */
export const PUBLIC_ERROR_CODES = [
  'bad_request',
  'not_found',
  'method_not_allowed',
  'origin_not_allowed',
  'app_check_failed',
  'rate_limited',
  'no_estimate',
  'invalid_code',
  'code_expired',
  'code_used',
  'too_many_attempts',
  'booking_token_invalid',
  'booking_rejected',
  'not_configured',
  'internal',
] as const;

// ---- GET /v1/branches ------------------------------------------------------------------------

export const publicBranchSchema = z
  .object({
    code: branchCodeSchema,
    name: z.string(),
    country: z.object({ code: sectorCodeSchema, name: z.string() }).strict(),
    /** `origin`: where a customer drops a box or asks for a pickup. `destination`: a hub in the Philippines. */
    side: z.enum(['origin', 'destination']),
    city: z.string().nullable(),
    address: z.string().nullable(),
    hours: z.string().nullable(),
    /** The branch's public number. Null when none is set: hide the call button. */
    phone: z.string().nullable(),
    /** Null when none is set: hide the WhatsApp button. */
    whatsapp: z.string().nullable(),
    geo: z.object({ lat: z.number(), lng: z.number() }).strict().nullable(),
  })
  .strict();
export const publicBranchesResponseSchema = z
  .object({ ok: z.literal(true), branches: z.array(publicBranchSchema) })
  .strict();

// ---- GET /v1/box-sizes ---------------------------------------------------------------------

export const boxSizesQuerySchema = z
  .object({ branch: branchCodeSchema.optional(), country: sectorCodeSchema.optional() })
  .strict()
  .refine((q) => q.branch === undefined || q.country === undefined, {
    message: 'Give a branch or a country, not both',
  });

export const publicBoxSizeSchema = z
  .object({
    code: sizeCodeSchema,
    name: z.string(),
    kind: z.enum(['standard', 'odd']),
    dimsCm: z.object({ l: z.number(), w: z.number(), h: z.number() }).strict().nullable(),
    maxWeightKg: z.number().nullable(),
    /** What an empty box of this size costs at the branch asked for; null without a branch or a price. */
    emptyBoxPrice: z
      .object({ amountMinor: z.number().int(), currency: z.string() })
      .strict()
      .nullable(),
  })
  .strict();

/** An add-on as the rate card lists it: a percentage of the declared value, or an amount. */
export const publicAddOnSchema = z
  .object({
    code: z.string(),
    label: z.string(),
    kind: z.enum(['percent_of_declared', 'per_shipment', 'per_box']),
    /** Percent (like 1 for 1%) for `percent_of_declared`; otherwise an amount in minor units. */
    value: z.number(),
    minChargeMinor: z.number().int().nullable(),
    currency: z.string(),
    services: z.array(z.enum(SERVICES)),
  })
  .strict();
export const publicBoxSizesResponseSchema = z
  .object({
    ok: z.literal(true),
    sizes: z.array(publicBoxSizeSchema),
    addOns: z.array(publicAddOnSchema),
  })
  .strict();

// ---- GET /v1/transit-times ------------------------------------------------------------------

export const transitTimesQuerySchema = z
  .object({ country: z.enum(['PH', 'IN']).optional(), service: z.enum(SERVICES).optional() })
  .strict();

export const publicTransitTimeSchema = z
  .object({
    country: z.enum(['PH', 'IN']),
    service: z.enum(SERVICES),
    minDays: z.number().int().positive(),
    maxDays: z.number().int().positive(),
    /**
     * Where the number comes from: `sla` (a door-to-door target set by EcoBox), `partner_sla` (the
     * courier's promised days) or `coverage` (the days from our hub to the door).
     */
    basis: z.enum(['sla', 'partner_sla', 'coverage']),
    /** For `coverage`: the days by province or region. */
    regions: z
      .array(
        z
          .object({
            region: z.string(),
            minDays: z.number().int().positive(),
            maxDays: z.number().int().positive(),
            cities: z.number().int().positive(),
          })
          .strict(),
      )
      .max(100),
  })
  .strict();
export const publicTransitTimesResponseSchema = z
  .object({ ok: z.literal(true), transitTimes: z.array(publicTransitTimeSchema) })
  .strict();

// ---- GET /v1/reviews ------------------------------------------------------------------------

export const reviewsQuerySchema = z
  .object({
    country: sectorCodeSchema.optional(),
    limit: z.coerce.number().int().min(1).max(20).optional(),
  })
  .strict();

/** A customer's own words, shown only because they agreed (4 or 5 stars). Their first name only. */
export const publicReviewSchema = z
  .object({
    stars: z.number().int().min(4).max(5),
    comment: z.string(),
    firstName: z.string(),
    date: z.iso.date(),
  })
  .strict();
export const publicReviewsResponseSchema = z
  .object({
    ok: z.literal(true),
    /** How many reviews have been published, and their average. With few, the site shows none. */
    count: z.number().int().nonnegative(),
    average: z.number().nullable(),
    reviews: z.array(publicReviewSchema),
  })
  .strict();

// ---- GET /v1/destinations --------------------------------------------------------------------

export const DESTINATION_COUNTRIES = ['PH', 'IN'] as const;

export const destinationsQuerySchema = z
  .object({
    q: z.string().trim().max(60).optional(),
    country: z.enum(DESTINATION_COUNTRIES).optional(),
    limit: z.coerce.number().int().min(1).max(50).optional(),
  })
  .strict();

export const serviceAvailabilitySchema = z.object({ available: z.boolean() }).strict();

export const publicDestinationSchema = z
  .object({
    id: slugSchema,
    country: z.string(),
    city: z.string(),
    province: z.string(),
    displayName: z.string(),
    services: z
      .object({
        sea: serviceAvailabilitySchema,
        air: serviceAvailabilitySchema,
        prime: serviceAvailabilitySchema,
      })
      .strict(),
  })
  .strict();
export const publicDestinationsResponseSchema = z
  .object({ ok: z.literal(true), destinations: z.array(publicDestinationSchema) })
  .strict();

// ---- POST /v1/quote --------------------------------------------------------------------------

export const quoteItemSchema = z
  .object({
    sizeId: sizeCodeSchema.optional(),
    weightKg: z.number().positive().max(500).optional(),
    count: z.number().int().min(1).max(50).default(1),
  })
  .strict()
  .refine((i) => i.sizeId !== undefined || i.weightKg !== undefined, {
    message: 'Give a box size or a weight',
  });

export const publicQuoteInputSchema = z
  .object({
    originBranchId: branchCodeSchema.optional(),
    originCountry: sectorCodeSchema.optional(),
    service: z.enum(SERVICES),
    destinationCityId: slugSchema,
    items: z.array(quoteItemSchema).min(1).max(20),
    declaredValueMinor: z.number().int().nonnegative().max(1e12).optional(),
    addOns: z
      .array(z.string().regex(/^[a-z_]{2,30}$/))
      .max(10)
      .optional(),
  })
  .strict()
  .refine((q) => (q.originBranchId === undefined) !== (q.originCountry === undefined), {
    message: 'Give an origin branch or an origin country, not both',
  });
export type PublicQuoteInput = z.infer<typeof publicQuoteInputSchema>;

export const publicQuoteResponseSchema = z
  .object({
    ok: z.literal(true),
    estimate: z.literal(true),
    label: z.string(),
    currency: z.string(),
    totalMinor: z.number().int(),
    vatIncludedMinor: z.number().int(),
    lines: z
      .array(
        z
          .object({
            kind: z.enum(['freight', 'addon', 'discount']),
            label: z.string(),
            amountMinor: z.number().int(),
          })
          .strict(),
      )
      .max(300),
    boxes: z.number().int().positive(),
    notes: z.array(z.string()),
  })
  .strict();

// ---- GET /v1/seasons -------------------------------------------------------------------------

export const publicSeasonSchema = z
  .object({
    id: slugSchema,
    name: z.string(),
    deliverBy: z.iso.date(),
    colour: z.string(),
    cutoffs: z.array(
      z
        .object({
          branchCode: branchCodeSchema,
          branchName: z.string(),
          country: sectorCodeSchema,
          service: z.enum(SERVICES),
          lastCollectDate: z.iso.date(),
          lastLoadDate: z.iso.date().nullable(),
          /** Whole days from today (the branch's date) to the last collection day; negative once passed. */
          daysLeft: z.number().int(),
          status: z.enum(['open', 'closing_soon', 'closed']),
        })
        .strict(),
    ),
  })
  .strict();
export const publicSeasonsResponseSchema = z
  .object({ ok: z.literal(true), asOf: iso, seasons: z.array(publicSeasonSchema) })
  .strict();

// ---- Tracking --------------------------------------------------------------------------------

export const publicTrackInputSchema = z
  .object({
    trackingNo: trackingNoSchema,
    /** The last four digits of the consignee's or the shipper's phone. */
    phoneLast4: z.string().regex(/^\d{4}$/, 'Four digits'),
  })
  .strict();

export const PUBLIC_STATUSES = [
  'booked',
  'at_origin',
  'in_transit',
  'arrived',
  'out_for_delivery',
  'partially_delivered',
  'delivered',
  'returning',
  'returned',
  'cancelled',
  'lost',
] as const;

/** No names, no phone numbers, no addresses and no amounts, by construction. */
export const publicTrackResponseSchema = z
  .object({
    ok: z.literal(true),
    trackingNo: trackingNoSchema,
    service: z.enum(SERVICES),
    boxes: z.number().int().nonnegative(),
    destinationCity: z.string(),
    status: z.object({ key: z.enum(PUBLIC_STATUSES), label: z.string() }).strict(),
    milestones: z.array(z.object({ key: z.string(), label: z.string(), at: iso }).strict()),
    currentState: z
      .object({ since: iso, hours: z.number().int().nonnegative() })
      .strict()
      .nullable(),
    /** An expected window. Until real history exists it is built from the delivery target. */
    predicted: z
      .object({
        from: z.iso.date(),
        to: z.iso.date(),
        basis: z.enum(['target', 'history']),
      })
      .strict()
      .nullable(),
    asOf: iso,
  })
  .strict();
export type PublicTrackResponse = z.infer<typeof publicTrackResponseSchema>;

export const publicTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{16,64}$/);

// ---- Pickup booking with phone OTP -----------------------------------------------------------

/**
 * `booking` answers a booking token for the website's pickup form. `login` answers a Firebase custom
 * token for the customer app (`loginVerifyResponseSchema` in customer.ts, 07 §7.1).
 */
export const OTP_PURPOSES = ['booking', 'login'] as const;

export const otpStartInputSchema = z
  .object({ phoneE164: publicPhoneSchema, purpose: z.enum(OTP_PURPOSES) })
  .strict();
export const otpStartResponseSchema = z
  .object({
    ok: z.literal(true),
    expiresInSeconds: z.number().int().positive(),
    resendAfterSeconds: z.number().int().positive(),
  })
  .strict();

export const otpVerifyInputSchema = z
  .object({
    phoneE164: publicPhoneSchema,
    purpose: z.enum(OTP_PURPOSES),
    code: z.string().regex(/^\d{6}$/, 'Six digits'),
  })
  .strict();
export const otpVerifyResponseSchema = z
  .object({
    ok: z.literal(true),
    bookingToken: z.string(),
    expiresInSeconds: z.number().int().positive(),
  })
  .strict();

export const publicPickupInputSchema = z
  .object({
    bookingToken: z.string().min(20).max(600),
    /** Makes the request safe to send twice: the same id returns the same reference. */
    clientRequestId: z.string().regex(/^[A-Za-z0-9_-]{8,40}$/),
    branchId: branchCodeSchema,
    name: z.string().trim().min(2).max(120),
    address: z
      .object({
        line1: z.string().trim().min(3).max(200),
        line2: z.string().max(200).default(''),
        city: z.string().trim().min(2).max(80),
        landmark: z.string().max(200).default(''),
      })
      .strict(),
    geo: z
      .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
      .strict()
      .optional(),
    window: z
      .object({
        date: z.iso.date(),
        from: z.string().regex(/^\d{2}:\d{2}$/),
        to: z.string().regex(/^\d{2}:\d{2}$/),
      })
      .strict(),
    wants: z
      .object({
        emptyBoxes: z
          .array(
            z.object({ sizeId: sizeCodeSchema, qty: z.number().int().min(1).max(50) }).strict(),
          )
          .max(10)
          .default([]),
        cargoPickup: z.boolean().default(true),
        estimatedBoxes: z.number().int().min(0).max(200).default(0),
      })
      .strict(),
    notes: z.string().max(500).default(''),
    /** The campaign code from the link or ad the visitor came through (15 §15.2). */
    campaignCode: campaignCodeSchema.optional(),
  })
  .strict();
export type PublicPickupInput = z.infer<typeof publicPickupInputSchema>;

export const publicPickupResponseSchema = z
  .object({
    ok: z.literal(true),
    reference: z.string(),
    status: z.literal('requested'),
    /** Present on a repeat of the same `clientRequestId`. */
    repeat: z.boolean().optional(),
  })
  .strict();

/** The booking token's signed contents. */
export const bookingTokenClaimsSchema = z
  .object({
    sub: e164Schema,
    purpose: z.enum(OTP_PURPOSES),
    iat: z.number().int(),
    exp: z.number().int(),
    jti: z.string(),
  })
  .strict();
export type BookingTokenClaims = z.infer<typeof bookingTokenClaimsSchema>;

export type PublicBranch = z.infer<typeof publicBranchSchema>;
export type PublicBoxSizesResponse = z.infer<typeof publicBoxSizesResponseSchema>;
export type PublicTransitTimesResponse = z.infer<typeof publicTransitTimesResponseSchema>;
export type PublicReviewsResponse = z.infer<typeof publicReviewsResponseSchema>;
export type PublicDestination = z.infer<typeof publicDestinationSchema>;
export type PublicSeason = z.infer<typeof publicSeasonSchema>;
export type PublicQuoteResponse = z.infer<typeof publicQuoteResponseSchema>;
export type OtpStartResponse = z.infer<typeof otpStartResponseSchema>;
export type OtpVerifyResponse = z.infer<typeof otpVerifyResponseSchema>;
export type PublicPickupResponse = z.infer<typeof publicPickupResponseSchema>;
